import { useState } from 'react';

import { useCreateSlaPolicy, useSlaPolicies, type SLAPolicy } from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  type Column,
  DataTable,
  DrawerPanel,
  Field,
  IconButton,
  Input,
  PageHeader,
  Toggle,
} from '../../components/ui/index.js';

function fmtMin(m: number | null): string {
  if (m == null) return '—';
  if (m < 60) return `${m} мин`;
  if (m < 1440) return `${Math.round((m / 60) * 10) / 10} ч`;
  return `${Math.round((m / 1440) * 10) / 10} д`;
}

export function SettingsSla(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const slaQ = useSlaPolicies(slug);
  const createSla = useCreateSlaPolicy(slug);

  const [showCreate, setShowCreate] = useState(false);
  const [slaName, setSlaName] = useState('');
  const [frt, setFrt] = useState('60');
  const [nrt, setNrt] = useState('');
  const [res, setRes] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  async function handleCreate(): Promise<void> {
    if (!slaName.trim()) return;
    await createSla.mutateAsync({
      name: slaName.trim(),
      first_reply_minutes: Number(frt),
      next_reply_minutes: nrt ? Number(nrt) : undefined,
      resolution_minutes: res ? Number(res) : undefined,
      is_default: isDefault,
    });
    setShowCreate(false);
    setSlaName('');
    setFrt('60');
    setNrt('');
    setRes('');
    setIsDefault(false);
  }

  const columns: Column<SLAPolicy>[] = [
    {
      key: 'name',
      label: 'Название',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 500 }}>{r.name}</span>
          {r.isDefault && (
            <span className="inbx-pill-badge inbx-pill-badge--info" style={{ marginLeft: 6 }}>
              по умолчанию
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'desc',
      label: 'Описание',
      render: (r) => (
        <span style={{ color: 'var(--inbx-text-tertiary)' }}>{r.description ?? '—'}</span>
      ),
    },
    { key: 'frt', label: 'Первый ответ', width: 130, render: (r) => fmtMin(r.firstReplyMinutes) },
    {
      key: 'res',
      label: 'Решение',
      width: 130,
      render: (r) => fmtMin(r.resolutionMinutes ?? null),
    },
    {
      key: 'status',
      label: 'Статус',
      width: 100,
      render: (r) => (
        <span className={`inbx-pill-badge inbx-pill-badge--${r.isActive ? 'success' : 'default'}`}>
          {r.isActive ? 'активна' : 'выключена'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 100,
      render: () => (
        <span style={{ display: 'flex', gap: 2 }}>
          <IconButton icon="ti-edit" tooltip="Редактировать" />
          <IconButton icon="ti-trash" tooltip="Удалить" />
        </span>
      ),
    },
  ];

  return (
    <div className="inbx-page">
      <PageHeader
        title="SLA-политики"
        actions={
          <Button variant="primary" icon="ti-plus" onClick={() => setShowCreate(true)}>
            Добавить SLA
          </Button>
        }
      />
      <div className="inbx-page__body" style={{ padding: 0 }}>
        <DataTable rows={slaQ.data ?? []} columns={columns} loading={slaQ.isLoading} />
      </div>

      <DrawerPanel
        open={showCreate}
        title="Новая SLA-политика"
        onClose={() => setShowCreate(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={createSla.isPending || !slaName.trim()}
            >
              {createSla.isPending ? 'Создание…' : 'Создать'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Название" required>
            <Input
              value={slaName}
              onChange={(e) => setSlaName(e.target.value)}
              placeholder="Стандартный SLA"
              autoFocus
            />
          </Field>
          <Field label="Первый ответ (мин)" required hint="например: 60 = 1 час">
            <Input type="number" min={1} value={frt} onChange={(e) => setFrt(e.target.value)} />
          </Field>
          <Field label="Следующий ответ (мин)" hint="необязательно">
            <Input
              type="number"
              min={1}
              value={nrt}
              onChange={(e) => setNrt(e.target.value)}
              placeholder="480"
            />
          </Field>
          <Field label="Решение (мин)" hint="необязательно">
            <Input
              type="number"
              min={1}
              value={res}
              onChange={(e) => setRes(e.target.value)}
              placeholder="1440"
            />
          </Field>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Toggle
              checked={isDefault}
              onChange={() => setIsDefault(!isDefault)}
              label="По умолчанию"
            />
          </div>
        </div>
      </DrawerPanel>
    </div>
  );
}
